import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../shared/auth/AuthContext';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';
import { Badge, Button, Card, Input, Select } from '@nexora/design-system';
import { PwaInstallPrompt } from './components/PwaInstallPrompt';
import { formatCurrency } from '../../shared/utils/format';

/**
 * Fechamento de caixa.
 *
 * A tela lia `CashSession[]` de `GET /pos/sessions`, que devolve
 * `{ items, total, offset, limit }` com entidades dentro — **a lista nunca
 * renderizou**. E declarava `terminalName`, `currentBalance`, `salesCount` e
 * `salesTotal`, que a entidade não tem. O handler não declarava tipo de
 * retorno, então `check:api` não comparava nada.
 *
 * O relatório da referência (`erpone-relat-rio-de-fechamento-de-dia`) sai de
 * `GET /pos/sessions/:id/summary`, que já existia e nenhuma tela chamava: ele
 * traz o resumo por meio de pagamento, as movimentações físicas e as vendas
 * da sessão.
 */

/** Espelha `CashSessionResponseDto`. */
interface CashSession {
  id: string;
  terminalId: string;
  terminalName: string | null;
  branchId: string;
  status: string;
  openingBalance: number;
  openedAt: string;
  openedById: string;
  closingExpected: number | null;
  closingActual: number | null;
  difference: number | null;
  closedAt: string | null;
  closedById: string | null;
  salesCount: number;
  salesTotal: number;
  movementsBalance: number;
}

interface PaginatedSessions {
  data: CashSession[];
  total: number;
  offset: number;
  limit: number;
}

/** Espelha `CashMovementResponseDto`. */
interface CashMovement {
  id: string;
  type: string;
  value: number;
  reason: string;
  authorizedByName: string;
  createdAt: string;
}

/** Espelha `SessionSummaryResponseDto`. */
interface SessionSummary {
  sessionId: string;
  terminalId: string;
  terminalName: string;
  openedBy: string;
  openedAt: string;
  openingBalance: number;
  status: string;
  closingExpected: number | null;
  closingActual: number | null;
  difference: number | null;
  closedAt: string | null;
  closedBy: string | null;
  totalSalesValue: number;
  totalSalesCount: number;
  totalDiscountValue: number;
  totalMovements: CashMovement[];
  sales: { saleId: string; number: string; grandTotal: number; discountValue: number; status: string; createdAt: string }[];
  paymentBreakdown: Record<string, number>;
}

interface Terminal {
  id: string;
  code: string;
  name: string;
  status: string;
}

/** Os quatro valores de `CashMovementType`. */
const MOVIMENTO_LABEL: Record<string, string> = {
  ENTRY: 'Entrada',
  SUPPLY: 'Suprimento',
  WITHDRAWAL: 'Retirada',
  DRAIN: 'Sangria',
};

/** Entra no caixa. */
const ENTRAM = new Set(['ENTRY', 'SUPPLY']);

/** Rótulos dos meios de pagamento que o PDV registra. */
const PAGAMENTO_LABEL: Record<string, string> = {
  CASH: 'Dinheiro',
  CREDIT: 'Cartão de crédito',
  CREDIT_CARD: 'Cartão de crédito',
  DEBIT: 'Cartão de débito',
  DEBIT_CARD: 'Cartão de débito',
  PIX: 'PIX',
  VOUCHER: 'Voucher',
  STORE_CREDIT: 'Crédito na loja',
  OTHER: 'Outros',
};

function dataHora(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR');
}

export function PosSessionPage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';
  const qc = useQueryClient();

  const [terminalId, setTerminalId] = useState('');
  const [saldoInicial, setSaldoInicial] = useState('');
  const [saldoContado, setSaldoContado] = useState('');
  const [movimento, setMovimento] = useState({ type: 'SUPPLY', value: '', reason: '' });
  const [sessaoAberta, setSessaoAberta] = useState<string | null>(null);
  const [aviso, setAviso] = useState<{ tipo: 'success' | 'error'; texto: string } | null>(null);

  const sessoesQ = useQuery({
    queryKey: queryKeys.pos.sessions(tenantId),
    queryFn: async () => apiClient.get<PaginatedSessions>('/pos/sessions'),
    enabled: !!tenantId,
  });

  const terminaisQ = useQuery({
    queryKey: ['pos', 'terminals', 'sessao'],
    queryFn: async () => apiClient.get<{ data: Terminal[] } | Terminal[]>('/pos/terminals'),
    enabled: !!tenantId,
  });

  const terminais = useMemo(() => {
    const bruto = terminaisQ.data;
    if (!bruto) return [];
    if (Array.isArray(bruto)) return bruto;
    return bruto.data ?? [];
  }, [terminaisQ.data]);

  const sessoes = useMemo(() => sessoesQ.data?.data ?? [], [sessoesQ.data]);
  const aberta = sessoes.find((s) => s.status === 'OPEN');
  const fechadas = sessoes.filter((s) => s.status !== 'OPEN');

  /** A sessão do relatório: a aberta, ou a que o operador escolher. */
  const sessaoDoRelatorio = sessaoAberta ?? aberta?.id ?? fechadas[0]?.id ?? null;

  const resumoQ = useQuery({
    queryKey: ['pos', 'sessions', sessaoDoRelatorio, 'summary'],
    queryFn: async () => apiClient.get<SessionSummary>(`/pos/sessions/${sessaoDoRelatorio}/summary`),
    enabled: !!sessaoDoRelatorio,
  });

  const resumo = resumoQ.data;

  function invalidar(): void {
    qc.invalidateQueries({ queryKey: queryKeys.pos.sessions(tenantId) }).catch(() => {});
    qc.invalidateQueries({ queryKey: ['pos', 'sessions'] }).catch(() => {});
  }

  const abrir = useMutation({
    /* `OpenCashSessionRequestDto` exige `terminalId` — a tela mandava só
       `openingBalance`, e abrir sessão nunca funcionou. */
    mutationFn: async () =>
      apiClient.post('/pos/sessions', {
        terminalId,
        openingBalance: Number(saldoInicial.replace(',', '.')) || 0,
      }),
    onSuccess: () => {
      setAviso({ tipo: 'success', texto: 'Sessão aberta.' });
      setSaldoInicial('');
      invalidar();
    },
    onError: (erro: Error) => setAviso({ tipo: 'error', texto: erro.message }),
  });

  const fechar = useMutation({
    /* `CloseCashSessionRequestDto` tem apenas `closingBalance`; a tela mandava
       também um `note`, que o contrato não tem e o servidor ignorava. */
    mutationFn: async (sessionId: string) =>
      apiClient.post(`/pos/sessions/${sessionId}/close`, {
        closingBalance: Number(saldoContado.replace(',', '.')) || 0,
      }),
    onSuccess: () => {
      setAviso({ tipo: 'success', texto: 'Caixa fechado.' });
      setSaldoContado('');
      invalidar();
    },
    onError: (erro: Error) => setAviso({ tipo: 'error', texto: erro.message }),
  });

  const lancarMovimento = useMutation({
    /* `POST /pos/sessions/:id/movement` existia e nenhuma tela chamava — e é
       a "Movimentação de Caixa (Físico)" da referência. */
    mutationFn: async (sessionId: string) =>
      apiClient.post(`/pos/sessions/${sessionId}/movement`, {
        type: movimento.type,
        value: Number(movimento.value.replace(',', '.')) || 0,
        reason: movimento.reason.trim(),
      }),
    onSuccess: () => {
      setAviso({ tipo: 'success', texto: 'Movimentação registrada.' });
      setMovimento({ type: 'SUPPLY', value: '', reason: '' });
      invalidar();
    },
    onError: (erro: Error) => setAviso({ tipo: 'error', texto: erro.message }),
  });

  /* ── Derivações do relatório ── */
  const totalPagamentos = Object.values(resumo?.paymentBreakdown ?? {}).reduce((s, v) => s + v, 0);
  const liquido = resumo ? resumo.totalSalesValue - resumo.totalDiscountValue : 0;
  const ticket = resumo && resumo.totalSalesCount > 0 ? liquido / resumo.totalSalesCount : null;
  const vendasEmDinheiro = resumo?.paymentBreakdown['CASH'] ?? 0;
  /* `totalSalesCount` conta as concluidas; a lista mostra as canceladas
     tambem. Sem dizer quantas, os dois numeros parecem se contradizer. */
  const canceladas = (resumo?.sales ?? []).filter((v) => v.status === 'CANCELLED').length;

  const entradas = (resumo?.totalMovements ?? []).filter((m) => ENTRAM.has(m.type));
  const saidas = (resumo?.totalMovements ?? []).filter((m) => !ENTRAM.has(m.type));
  const totalEntradas = entradas.reduce((s, m) => s + m.value, 0);
  const totalSaidas = saidas.reduce((s, m) => s + m.value, 0);

  return (
    <div className="ps-page">
      <div className="ps-header">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Fechamento de Caixa</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Abertura, movimentação física e fechamento — com o resumo da sessão
            como o servidor o apura.
          </p>
        </div>
        {sessoes.length > 1 && (
          <div className="ps-picker">
            <Select
              label="Sessão"
              value={sessaoDoRelatorio ?? ''}
              onChange={(e) => setSessaoAberta(e.target.value)}
            >
              {sessoes.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.terminalName ?? s.terminalId} · {dataHora(s.openedAt)}
                  {s.status === 'OPEN' ? ' (aberta)' : ''}
                </option>
              ))}
            </Select>
          </div>
        )}
      </div>

      <PwaInstallPrompt />

      {aviso && (
        <div className={`ps-aviso ps-aviso--${aviso.tipo}`} role="alert">
          {aviso.texto}
          <button onClick={() => setAviso(null)} aria-label="Fechar" className="ps-aviso__close">
            &times;
          </button>
        </div>
      )}

      {sessoesQ.error && (
        <Card>
          <div className="ps-card">
            <p className="ps-erro">
              Não foi possível carregar as sessões: {(sessoesQ.error as Error).message}
            </p>
          </div>
        </Card>
      )}

      {/* ── Abertura, quando não há sessão aberta ── */}
      {!aberta && !sessoesQ.isLoading && (
        <Card>
          <div className="ps-card">
            <h3 className="ps-section">Abrir caixa</h3>
            <div className="ps-form">
              <Select
                label="Terminal"
                value={terminalId}
                onChange={(e) => setTerminalId(e.target.value)}
              >
                <option value="">Selecione…</option>
                {terminais
                  .filter((t) => t.status !== 'REVOKED')
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.code} — {t.name}
                    </option>
                  ))}
              </Select>
              <Input
                label="Saldo inicial (fundo de troco)"
                type="number"
                min={0}
                step="0.01"
                placeholder="0,00"
                value={saldoInicial}
                onChange={(e) => setSaldoInicial(e.target.value)}
              />
              <Button
                variant="primary"
                loading={abrir.isPending}
                disabled={!terminalId}
                onClick={() => abrir.mutate()}
              >
                Abrir caixa
              </Button>
            </div>
            {terminais.length === 0 && (
              <p className="ps-hint">
                Nenhum terminal cadastrado. O caixa é aberto em um terminal —
                cadastre em <strong>Terminais</strong>.
              </p>
            )}
          </div>
        </Card>
      )}

      {/* ── Indicadores do fechamento ── */}
      {resumo && (
        <>
          <div className="ps-kpis">
            <Card>
              <div className="ps-kpi">
                <span className="ps-kpi__label">Total bruto</span>
                <strong className="ps-kpi__value">{formatCurrency(resumo.totalSalesValue)}</strong>
                <span className="ps-kpi__foot">
                  {resumo.totalSalesCount} venda{resumo.totalSalesCount === 1 ? '' : 's'}
                  {canceladas > 0
                    ? ` · ${canceladas} cancelada${canceladas === 1 ? '' : 's'}`
                    : ''}
                </span>
              </div>
            </Card>
            <Card>
              <div className="ps-kpi">
                <span className="ps-kpi__label">Total líquido</span>
                <strong className="ps-kpi__value">{formatCurrency(liquido)}</strong>
                <span className="ps-kpi__foot">
                  desconto {formatCurrency(resumo.totalDiscountValue)}
                </span>
              </div>
            </Card>
            <Card>
              <div className="ps-kpi">
                <span className="ps-kpi__label">Ticket médio</span>
                <strong className="ps-kpi__value">
                  {ticket === null ? '—' : formatCurrency(ticket)}
                </strong>
                <span className="ps-kpi__foot">líquido ÷ número de vendas</span>
              </div>
            </Card>
            <Card>
              <div className="ps-kpi">
                <span className="ps-kpi__label">Situação</span>
                <strong className="ps-kpi__value ps-kpi__value--sm">
                  <Badge variant={resumo.status === 'OPEN' ? 'success' : 'neutral'} size="sm" dot>
                    {resumo.status === 'OPEN' ? 'Aberto' : 'Fechado'}
                  </Badge>
                </strong>
                <span className="ps-kpi__foot">
                  {resumo.terminalName} · {resumo.openedBy}
                </span>
              </div>
            </Card>
          </div>

          <div className="ps-cols">
            {/* ── Meios de pagamento ── */}
            <Card>
              <div className="ps-card">
                <h3 className="ps-section">Resumo por meio de pagamento</h3>
                {Object.keys(resumo.paymentBreakdown).length === 0 ? (
                  <p className="ps-muted">Nenhum pagamento registrado nesta sessão.</p>
                ) : (
                  <table className="ps-table">
                    <thead>
                      <tr>
                        <th>Meio</th>
                        <th className="ps-num">Valor</th>
                        <th className="ps-num">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(resumo.paymentBreakdown)
                        .sort(([, a], [, b]) => b - a)
                        .map(([meio, valor]) => (
                          <tr key={meio}>
                            <td>{PAGAMENTO_LABEL[meio] ?? meio}</td>
                            <td className="ps-num">{formatCurrency(valor)}</td>
                            <td className="ps-num">
                              {totalPagamentos > 0
                                ? `${((valor / totalPagamentos) * 100).toFixed(1).replace('.', ',')}%`
                                : '—'}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td>Total</td>
                        <td className="ps-num">{formatCurrency(totalPagamentos)}</td>
                        <td className="ps-num">100%</td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            </Card>

            {/* ── Movimentação física ── */}
            <Card>
              <div className="ps-card">
                <h3 className="ps-section">Movimentação de caixa (físico)</h3>
                <table className="ps-table">
                  <tbody>
                    <tr>
                      <td>Saldo inicial</td>
                      <td className="ps-num">{formatCurrency(resumo.openingBalance)}</td>
                    </tr>
                    <tr>
                      <td>Entradas e suprimentos</td>
                      <td className="ps-num">
                        {totalEntradas > 0 ? `+ ${formatCurrency(totalEntradas)}` : formatCurrency(0)}
                      </td>
                    </tr>
                    <tr>
                      <td>Retiradas e sangrias</td>
                      <td className="ps-num">
                        {totalSaidas > 0 ? `− ${formatCurrency(totalSaidas)}` : formatCurrency(0)}
                      </td>
                    </tr>
                    <tr>
                      <td>Vendas em dinheiro</td>
                      <td className="ps-num">+ {formatCurrency(vendasEmDinheiro)}</td>
                    </tr>
                    <tr className="ps-total">
                      <td>Saldo final esperado</td>
                      <td className="ps-num">
                        {/* Do servidor: `closingExpected` é calculado no
                            fechamento. Enquanto aberto, a soma das partes. */}
                        {resumo.closingExpected !== null
                          ? formatCurrency(resumo.closingExpected)
                          : formatCurrency(
                              resumo.openingBalance + totalEntradas - totalSaidas + vendasEmDinheiro,
                            )}
                      </td>
                    </tr>
                    {resumo.closingActual !== null && (
                      <>
                        <tr>
                          <td>Contado no fechamento</td>
                          <td className="ps-num">{formatCurrency(resumo.closingActual)}</td>
                        </tr>
                        <tr className="ps-total">
                          <td>Diferença</td>
                          <td className="ps-num">
                            <span
                              className={
                                (resumo.difference ?? 0) === 0
                                  ? 'ps-flat'
                                  : (resumo.difference ?? 0) > 0
                                    ? 'ps-up'
                                    : 'ps-down'
                              }
                            >
                              {formatCurrency(resumo.difference ?? 0)}
                            </span>
                          </td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>

                {resumo.totalMovements.length > 0 && (
                  <table className="ps-table">
                    <thead>
                      <tr>
                        <th>Lançamento</th>
                        <th>Motivo</th>
                        <th>Autorizado por</th>
                        <th className="ps-num">Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resumo.totalMovements.map((m) => (
                        <tr key={m.id}>
                          <td>
                            {MOVIMENTO_LABEL[m.type] ?? m.type}
                            <small className="ps-sub">{dataHora(m.createdAt)}</small>
                          </td>
                          <td>{m.reason}</td>
                          <td>{m.authorizedByName}</td>
                          <td className="ps-num">
                            {ENTRAM.has(m.type) ? '+ ' : '− '}
                            {formatCurrency(m.value)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </Card>
          </div>

          {/* ── Ações da sessão aberta ── */}
          {resumo.status === 'OPEN' && (
            <div className="ps-cols">
              <Card>
                <div className="ps-card">
                  <h3 className="ps-section">Lançar movimentação</h3>
                  <div className="ps-form">
                    <Select
                      label="Tipo"
                      value={movimento.type}
                      onChange={(e) => setMovimento((m) => ({ ...m, type: e.target.value }))}
                    >
                      {/* Os quatro valores de `CashMovementType` — a referência
                          agrupa em dois, o domínio distingue quatro. */}
                      {Object.entries(MOVIMENTO_LABEL).map(([valor, rotulo]) => (
                        <option key={valor} value={valor}>
                          {rotulo}
                        </option>
                      ))}
                    </Select>
                    <Input
                      label="Valor"
                      type="number"
                      min={0}
                      step="0.01"
                      placeholder="0,00"
                      value={movimento.value}
                      onChange={(e) => setMovimento((m) => ({ ...m, value: e.target.value }))}
                    />
                    <Input
                      label="Motivo"
                      placeholder="Troco, depósito, retirada para banco…"
                      value={movimento.reason}
                      onChange={(e) => setMovimento((m) => ({ ...m, reason: e.target.value }))}
                    />
                    <Button
                      variant="secondary"
                      loading={lancarMovimento.isPending}
                      disabled={!movimento.value || !movimento.reason.trim()}
                      onClick={() => lancarMovimento.mutate(resumo.sessionId)}
                    >
                      Lançar
                    </Button>
                  </div>
                </div>
              </Card>

              <Card>
                <div className="ps-card">
                  <h3 className="ps-section">Fechar caixa</h3>
                  <p className="ps-hint">
                    Informe o dinheiro contado na gaveta. O servidor calcula a
                    diferença contra o esperado.
                  </p>
                  <div className="ps-form">
                    <Input
                      label="Dinheiro contado"
                      type="number"
                      min={0}
                      step="0.01"
                      placeholder="0,00"
                      value={saldoContado}
                      onChange={(e) => setSaldoContado(e.target.value)}
                    />
                    <Button
                      variant="primary"
                      loading={fechar.isPending}
                      disabled={!saldoContado}
                      onClick={() => fechar.mutate(resumo.sessionId)}
                    >
                      Fechar caixa
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* ── Vendas da sessão ── */}
          <Card>
            <div className="ps-card">
              <h3 className="ps-section">Vendas da sessão</h3>
              {resumo.sales.length === 0 ? (
                <p className="ps-muted">Nenhuma venda registrada.</p>
              ) : (
                <div className="ps-table-wrap">
                  <table className="ps-table">
                    <thead>
                      <tr>
                        <th>Venda</th>
                        <th>Hora</th>
                        <th>Situação</th>
                        <th className="ps-num">Desconto</th>
                        <th className="ps-num">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resumo.sales.map((v) => (
                        <tr key={v.saleId}>
                          <td className="ps-mono">{v.number}</td>
                          <td className="ps-mono">{dataHora(v.createdAt)}</td>
                          <td>
                            <Badge variant={v.status === 'CANCELLED' ? 'error' : 'success'} size="sm">
                              {v.status === 'CANCELLED' ? 'Cancelada' : 'Concluída'}
                            </Badge>
                          </td>
                          <td className="ps-num">
                            {v.discountValue > 0 ? `− ${formatCurrency(v.discountValue)}` : '—'}
                          </td>
                          <td className="ps-num">{formatCurrency(v.grandTotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </Card>

          {/* ── O que a referência pede e não tem fonte ── */}
          <Card>
            <div className="ps-card">
              <h3 className="ps-section">Fora deste relatório</h3>
              <ul className="ps-list">
                <li>
                  <strong>Comparação com o dia anterior</strong> (&quot;+12,5% vs. ontem&quot;) — o
                  resumo é de uma sessão; não há rota que compare sessões nem
                  apuração por dia.
                </li>
                <li>
                  <strong>Resumo de tributos da sessão</strong> — as vendas do PDV
                  não carregam abertura de tributo no resumo; o cálculo fiscal
                  fica no motor (Simulador de Impacto) e no documento emitido.
                </li>
                <li>
                  <strong>Vendas por operador</strong> — o resumo tem o operador que
                  <em> abriu</em> a sessão, não o de cada venda.
                </li>
                <li>
                  <strong>Exportar PDF</strong> — não há rota de relatório.
                </li>
              </ul>
            </div>
          </Card>
        </>
      )}

      {resumoQ.isLoading && sessaoDoRelatorio && (
        <Card>
          <div className="ps-card">
            <p className="ps-muted">Carregando o resumo da sessão…</p>
          </div>
        </Card>
      )}

      {!sessoesQ.isLoading && sessoes.length === 0 && (
        <Card>
          <div className="ps-card">
            <p className="ps-muted">
              Nenhuma sessão de caixa registrada. Abra o caixa para começar o dia.
            </p>
          </div>
        </Card>
      )}

      <style>{`
        .ps-page { padding: 24px; display: flex; flex-direction: column; gap: 16px; }
        .ps-header { display: flex; justify-content: space-between; align-items: flex-end; gap: 16px; flex-wrap: wrap; }
        .ps-picker { min-width: 280px; }
        .ps-card { padding: 16px; display: flex; flex-direction: column; gap: 12px; }
        .ps-section { font-family: Inter, sans-serif; font-size: 20px; line-height: 28px; font-weight: 600; color: #191c1e; margin: 0; }
        .ps-hint { font-size: 12px; line-height: 18px; color: #43474e; margin: 0; }
        .ps-muted { font-size: 12px; color: #74777f; margin: 0; }
        .ps-erro { font-size: 12px; color: #ba1a1a; margin: 0; }
        .ps-form { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 12px; align-items: end; }
        .ps-kpis { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
        @media (max-width: 1100px) { .ps-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        @media (max-width: 640px) { .ps-kpis { grid-template-columns: 1fr; } }
        .ps-kpi { padding: 16px; display: flex; flex-direction: column; gap: 4px; }
        .ps-kpi__label { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; text-transform: uppercase; letter-spacing: 0.02em; color: #43474e; }
        .ps-kpi__value { font-family: Inter, sans-serif; font-size: 24px; line-height: 32px; font-weight: 600; color: #191c1e; font-variant-numeric: tabular-nums; }
        .ps-kpi__value--sm { font-size: 16px; line-height: 32px; }
        .ps-kpi__foot { font-size: 12px; color: #74777f; }
        .ps-cols { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; align-items: start; }
        @media (max-width: 1000px) { .ps-cols { grid-template-columns: 1fr; } }
        .ps-table-wrap { overflow-x: auto; }
        .ps-table { width: 100%; border-collapse: collapse; }
        .ps-table th { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; font-weight: 500; letter-spacing: 0.02em; text-transform: uppercase; color: #43474e; text-align: left; padding: 8px 12px; border-bottom: 1px solid #c4c6cf; white-space: nowrap; }
        .ps-table td { padding: 8px 12px; border-bottom: 1px solid #eceef0; font-size: 12px; color: #191c1e; vertical-align: middle; }
        .ps-table tfoot td { font-weight: 600; border-top: 1px solid #c4c6cf; border-bottom: none; }
        .ps-total td { font-weight: 600; border-top: 1px solid #c4c6cf; }
        .ps-num { text-align: right; font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; white-space: nowrap; }
        .ps-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; white-space: nowrap; }
        .ps-sub { display: block; color: #74777f; font-size: 12px; }
        .ps-flat { color: #74777f; }
        .ps-up { color: #1a6b33; }
        .ps-down { color: #ba1a1a; }
        .ps-list { margin: 0; padding-left: 18px; font-size: 12px; line-height: 20px; color: #43474e; display: flex; flex-direction: column; gap: 6px; }
        .ps-aviso { display: flex; align-items: center; gap: 8px; padding: 8px 12px; border-radius: 4px; font-size: 12px; }
        .ps-aviso--success { background: #d3f5d6; border: 1px solid #b6ebbb; color: #2e7d32; }
        .ps-aviso--error { background: #ffdad6; border: 1px solid #ffb4ab; color: #ba1a1a; }
        .ps-aviso__close { margin-left: auto; background: none; border: none; cursor: pointer; color: inherit; font-size: 1rem; line-height: 1; }
      `}</style>
    </div>
  );
}
